import { useEffect, useState, useCallback, useMemo } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Title from "../../../../common/components/Title";
import { Room } from "../../../../../types/context";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Alert,
  Box,
  CircularProgress,
  Button,
  Modal,
  Typography,
  TextField,
  // Paper,
} from "@mui/material";
import RoomRow from "./RoomRow";
import { useUserContext } from "../../../../context/UserContext";
import { useApi } from "../../../../utils/api";

// Acá sacar las habitaciones y su data, tener tres opciones, un dropdown por año, mes y un datepicker para búsqueda exacta
// Mostrar las habitaciones, la fecha y su disponibilidad en una tabla
// Habilitar un botón de edición para los elementos de la tabla


export default function Rooms() {
  const { user } = useUserContext();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [availabilities, setAvailabilities] = useState<Array<Room>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [modalMonth, setModalMonth] = useState(month);
  const [modalYear, setModalYear] = useState(Number(year));
  const [defaultRooms, setDefaultRooms] = useState(5);
  const [allRooms, setAllRooms] = useState<any[]>([]);
  const { get, post } = useApi();

  const months = useMemo(() => [
    { value: 1, text: "Enero" },
    { value: 2, text: "Febrero" },
    { value: 3, text: "Marzo" },
    { value: 4, text: "Abril" },
    { value: 5, text: "Mayo" },
    { value: 6, text: "Junio" },
    { value: 7, text: "Julio" },
    { value: 8, text: "Agosto" },
    { value: 9, text: "Septiembre" },
    { value: 10, text: "Octubre" },
    { value: 11, text: "Noviembre" },
    { value: 12, text: "Diciembre" },
  ], []);

  const loadRooms = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      const query = `?month=${month}&year=${year}`;
      console.log(`Loading rooms for: ${query}`);
      console.log('User token:', user.token ? 'Present' : 'Missing');
      const data = await get(`room-availabilities${query}`);
      console.log('Rooms loaded:', data);
      setAvailabilities(data as Array<Room>);
    } catch (err) {
      console.error('Error al cargar habitaciones:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(`Error al cargar habitaciones: ${errorMessage}`);
      // Set empty array on error to prevent infinite loading
      setAvailabilities([]);
    } finally {
      setLoading(false);
    }
  }, [get, month, user, year]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const handleMonth = (event: SelectChangeEvent<number>) => {
    setMonth(Number(event.target.value));
  };

  const handleYear = (event: SelectChangeEvent) => {
    setYear(event.target.value);
  };

  const loadAllRooms = useCallback(async () => {
    try {
      const roomsData = await get('rooms');
      if (Array.isArray(roomsData)) {
        setAllRooms(roomsData);
      } else if (roomsData && roomsData.rooms) {
        setAllRooms(roomsData.rooms);
      } else {
        setAllRooms([]);
      }
    } catch (err) {
      console.error('Error loading rooms:', err);
      setAllRooms([]);
    }
  }, [get]);

  const openCreateModal = useCallback(async () => {
    await loadAllRooms();
    setModalMonth(month);
    setModalYear(Number(year));
    setDefaultRooms(5);
    setSelectedRoom(null);
    setOpenModal(true);
  }, [month, year, loadAllRooms]);

  const handleCreateDates = useCallback(async () => {
    if (!user || !selectedRoom) return;
    
    try {
      setCreating(true);
      setError(null);
      
      await post('room-availabilities/create-month', {
        room_id: selectedRoom.id,
        month: modalMonth,
        year: modalYear,
        default_rooms: defaultRooms
      });
      
      const monthName = months.find(m => m.value === modalMonth)?.text || modalMonth.toString();
      setSuccess(`Fechas disponibles creadas exitosamente para ${selectedRoom.name} - ${monthName} ${modalYear}`);
      
      setOpenModal(false);
      await loadRooms();
      
    } catch (err) {
      console.error('Error al crear fechas disponibles:', err);
      setError('Error al crear fechas disponibles. Verifica que el servidor esté funcionando.');
    } finally {
      setCreating(false);
    }
  }, [user, selectedRoom, modalMonth, modalYear, defaultRooms, post, months, loadRooms]);

  // Generar años dinámicamente: año actual + 5 años adicionales
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear + i);
    }
    return years;
  };

  const years = generateYears();

  return (
    <>
      <Title>Habitaciones</Title>
      {error && (
        <Box mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={2}>
          <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
        </Box>
      )}
      <Grid container>
        <Grid item>
          <FormControl sx={{ m: 1, maxWidth: 300 }} size='small'>
            <InputLabel id='mes-1'>Mes</InputLabel>
            <Select
              labelId='mes-1'
              id='mes-1'
              value={month}
              label='Mes'
              onChange={handleMonth}
            >
              {months &&
                months.map(({ value, text }) => (
                  <MenuItem key={`month-${value}`} value={value}>
                    {text}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl sx={{ m: 1, maxWidth: 300 }} size='small'>
            <InputLabel id='año-1'>Año</InputLabel>
            <Select
              labelId='año-1'
              id='año-1'
              value={year}
              label='Año'
              onChange={handleYear}
            >
              {years.map((yearValue) => (
                <MenuItem key={`year-${yearValue}`} value={String(yearValue)}>
                  {yearValue}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={openCreateModal}
            disabled={creating}
            sx={{ m: 1, height: 40 }}
          >
            Crear Fechas Disponibles
          </Button>
        </Grid>
      </Grid>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Habitación</TableCell>
            <TableCell>Fechas</TableCell>
            <TableCell>Cantidad de habitaciones</TableCell>
            <TableCell>Acción</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : availabilities && availabilities.length > 0 ? (
            availabilities.map((row) => (
              <RoomRow key={`${row.id}-${row.availability_id}`} room={row} />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No hay habitaciones disponibles para el período seleccionado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Modal para crear fechas disponibles */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="create-dates-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="create-dates-modal" variant="h6" component="h2" mb={2}>
            Crear Fechas Disponibles
          </Typography>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Habitación</InputLabel>
            <Select
              value={selectedRoom?.id || ''}
              onChange={(e) => {
                const room = allRooms.find(r => r.id === e.target.value);
                setSelectedRoom(room);
              }}
              label="Habitación"
            >
              {allRooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Mes</InputLabel>
            <Select
              value={modalMonth}
              onChange={(e) => setModalMonth(Number(e.target.value))}
              label="Mes"
            >
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Año"
            type="number"
            value={modalYear}
            onChange={(e) => setModalYear(Number(e.target.value))}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Habitaciones disponibles por defecto"
            type="number"
            value={defaultRooms}
            onChange={(e) => setDefaultRooms(Number(e.target.value))}
            inputProps={{ min: 1 }}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateDates}
              disabled={creating || !selectedRoom}
            >
              {creating ? 'Creando...' : 'Crear'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
